import { AutoField, useField } from "@w3rone/json-schema-form"
import type {
  FieldComponentProps,
  CollectionFieldSchema,
} from "@w3rone/json-schema-form"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Tooltip from "@mui/material/Tooltip"
import { FormErrors } from "./FormErrors"
import AddBox from "@mui/icons-material/AddBox"
import DeleteForever from "@mui/icons-material/DeleteForever"
import Info from "@mui/icons-material/Info"

export const CollectionTableField = ({ name }: FieldComponentProps) => {
  const {
    value,
    schema: schemaProps,
    onChange,
    errors,
    label,
    description,
  } = useField(name)
  assertCollectionValue(value)

  const schema = schemaProps as CollectionFieldSchema

  if (Array.isArray(schema.items)) {
    return null
  }

  if (schema.items.type !== "object") {
    return null
  }

  const handleAdd = () => {
    onChange?.([...(value || []), {}])
  }

  const handleRemove = (index: number) => {
    const defaultedValue = value || []

    onChange?.([
      ...defaultedValue.slice(0, index),
      undefined,
      ...defaultedValue.slice(index + 1),
    ])
  }

  const highlightedProperties = Object.entries(schema.items.properties).filter(
    ([, propertySchema]) => propertySchema.options?.highlighted,
  )

  const otherProperties = Object.entries(schema.items.properties).filter(
    ([, propertySchema]) => !propertySchema.options?.highlighted,
  )

  return (
    <FormControl
      variant="standard"
      component="fieldset"
      error={errors.length > 0}
      fullWidth={true}
    >
      {label ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormLabel component="legend">{label}</FormLabel>
          {description ? (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{ __html: description }} />}
            >
              <Info />
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
      <FormErrors errors={errors} />
      <Stack spacing={2}>
        {schema.options.collection.allowAdd && !schema.readOnly ? (
          <Button startIcon={<AddBox />} onClick={handleAdd}>
            Ajouter un élément
          </Button>
        ) : null}

        <TableContainer sx={{ maxHeight: "80vh" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {schema.options.collection.allowDelete && !schema.readOnly ? (
                  <TableCell sx={{ width: 50 }}></TableCell>
                ) : null}
                {[...highlightedProperties, ...otherProperties].map(
                  ([propertyName, propertySchema]) => (
                    <TableCell key={propertyName} sx={{ minWidth: "300px" }}>
                      {propertySchema.title}
                    </TableCell>
                  ),
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.isArray(value)
                ? value.map((itemValue, index) =>
                    itemValue === undefined ? null : (
                      <TableRow key={index}>
                        {schema.options.collection.allowDelete &&
                        !schema.readOnly ? (
                          <TableCell>
                            <IconButton
                              onClick={() => handleRemove(index)}
                              color="error"
                            >
                              <DeleteForever />
                            </IconButton>
                          </TableCell>
                        ) : null}
                        {[...highlightedProperties, ...otherProperties].map(
                          ([propertyName]) => (
                            <TableCell key={propertyName}>
                              <AutoField
                                name={`${name}[${index}][${propertyName}]`}
                              />
                            </TableCell>
                          ),
                        )}
                      </TableRow>
                    ),
                  )
                : null}
            </TableBody>
          </Table>
        </TableContainer>
        {Array.isArray(value) &&
        value.length > 0 &&
        schema.options.collection.allowAdd &&
        !schema.readOnly ? (
          <Button startIcon={<AddBox />} onClick={handleAdd}>
            Ajouter un élément
          </Button>
        ) : null}
      </Stack>
    </FormControl>
  )
}

function assertCollectionValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): asserts value is any[] | undefined {
  if (value && !Array.isArray(value)) {
    throw new Error("value is not a collection value")
  }
}
